import React, { useState, useRef } from 'react';
import {
  Upload, FileText, Loader2, Save, X, Key, CheckCircle2, Edit3,
  Sparkles, TrendingDown, AlertTriangle, BarChart3, Info, Lightbulb, ChevronDown, ChevronUp
} from 'lucide-react';
import { getGeminiKey, setGeminiKey } from '../modules/buildpro/BuildPro';
import { quotesApi, contactsApi } from '@/core/adapter-api';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from './shared';
import {
  calculateProjectSummary,
  calculateProjectWaste,
  identifyVETargets,
  type WasteDetail
} from '../services/ddcSkillsService';
import {
  explainBudgetItem,
  suggestAlternatives,
  getGroqKey
} from '../services/groqService';

interface BC3Item {
  cod: string;
  label: string;
  quantity: number;
  ud: string;
  p: number;
}

export const MedicionesPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<BC3Item[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  
  const [apiKeyInput, setApiKeyInput] = useState(getGeminiKey() || '');
  const [apiKeySaved, setApiKeySaved] = useState(false);
  const hasApiKey = !!getGeminiKey();
  
  // ── DDC Skills panels ──
  const [showSummary, setShowSummary] = useState(true);
  const [showWaste, setShowWaste] = useState(false);
  const [showVE, setShowVE] = useState(false);

  // ── Groq AI state ──
  const [aiPopover, setAiPopover] = useState<{ idx: number; type: 'explain' | 'alternatives'; content: string; loading: boolean } | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const { addToast } = useToast();
  const hasGroqKey = !!getGroqKey();

  const handleFile = (f: File) => {
    if (!f.name.toLowerCase().endsWith('.bc3')) {
      setError('Por favor, selecciona un archivo FIEBDC-3 (.bc3)');
      return;
    }
    setFile(f);
    setError(null);
    setItems([]);
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => setDragActive(false);

  const processFile = async () => {
    if (!file) return;
    const apiKey = getGeminiKey();
    if (!apiKey) {
      setError('Configura la API key de Google Gemini primero.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const text = await file.text();
      const truncatedText = text.length > 100000 ? text.substring(0, 100000) + '\n...[TRUNCATED]' : text;

      const prompt = `Eres un experto Aparejador (Quantity Surveyor). 
Aquí tienes el contenido en formato texto de un archivo modelo estándar español FIEBDC-3 (.bc3).
Tu objetivo es extraer las principales partidas (evita los capítulos raíz y céntrate en las partidas medibles con precio).
Devuelve ÚNICAMENTE un array JSON válido, sin markdown, sin bloque de código json, sólo el texto crudo del array.
Formato esperado para cada objeto del array:
{"cod": "código partida", "label": "descripción", "quantity": numero_medicion, "ud": "unidad", "p": precio_unitario}

TEXTO BC3:
${truncatedText}`;

      const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          generationConfig: { maxOutputTokens: 8192, temperature: 0.1 },
        }),
      });

      if (!res.ok) throw new Error('Error al procesar con Gemini API');
      
      const data = await res.json();
      const rawText = data.candidates?.[0]?.content?.parts?.[0]?.text;
      if (!rawText) throw new Error('Respuesta vacía de la IA');
      
      const cleanJson = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(cleanJson);
      
      if (Array.isArray(parsed)) {
        setItems(parsed);
        addToast(`${parsed.length} partidas extraídas correctamente`, 'success');
      } else {
        throw new Error('La IA no devolvió un array válido.');
      }
    } catch (e: any) {
      setError(e.message || 'Ocurrió un error al procesar el archivo.');
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (idx: number, field: keyof BC3Item, value: string | number) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      return { ...item, [field]: value };
    }));
  };

  // ── Totals ──
  const baseTotal = items.reduce((sum, item) => sum + (item.quantity || 0) * (item.p || 0), 0);
  const vatTotal = baseTotal * 0.21;
  const grandTotal = baseTotal + vatTotal;

  // ── DDC Project Summary ──
  const projectSummary = items.length > 0
    ? calculateProjectSummary(
        items.map(i => ({ name: i.label, quantity: i.quantity, unit: i.ud, price: i.p, total: (i.quantity || 0) * (i.p || 0) }))
      )
    : null;

  // ── DDC Waste Analysis ──
  const wasteAnalysis = items.length > 0
    ? calculateProjectWaste(
        items.map(i => ({ name: i.label, quantity: i.quantity, price: i.p }))
      )
    : null;

  // ── DDC VE Targets ──
  const veTargets = items.length > 0
    ? identifyVETargets(
        items.map(i => ({ name: i.label, quantity: i.quantity, price: i.p, total: (i.quantity || 0) * (i.p || 0) })),
        10,
        1.0
      )
    : [];

  // ── Groq AI handlers ──
  const handleExplain = async (idx: number) => {
    const item = items[idx];
    setAiPopover({ idx, type: 'explain', content: '', loading: true });
    try {
      const explanation = await explainBudgetItem(item.label, item.ud, item.p);
      setAiPopover({ idx, type: 'explain', content: explanation, loading: false });
    } catch (err: any) {
      setAiPopover({ idx, type: 'explain', content: `Error: ${err.message}`, loading: false });
    }
  };

  const handleSuggestAlternatives = async (idx: number) => {
    const item = items[idx];
    setAiPopover({ idx, type: 'alternatives', content: '', loading: true });
    try {
      const alternatives = await suggestAlternatives(item.label, item.p, item.ud);
      setAiPopover({ idx, type: 'alternatives', content: alternatives, loading: false });
    } catch (err: any) {
      setAiPopover({ idx, type: 'alternatives', content: `Error: ${err.message}`, loading: false });
    }
  };

  const saveToQuote = async () => {
    if (items.length === 0) return;
    setSaving(true);
    try {
      let aiClient = (await contactsApi.all()).find(c => c.name === 'BuildPro BC3');
      let clientId = aiClient?.id;
      if (!clientId) {
        clientId = await contactsApi.add({
          type: 'client',
          name: 'BuildPro BC3',
          nif: 'BC3-GEN',
          email: 'bc3@buildpro.app',
          phone: '', address: 'Importado de BC3', city: '', zip: '', province: '', country: '', notes: '',
          createdAt: Date.now(), updatedAt: Date.now()
        } as any);
      }

      const lines = items.map(item => ({
        id: uuidv4(),
        title: item.label,
        description: `Código: ${item.cod}`,
        quantity: item.quantity || 1,
        unit: item.ud || 'ud',
        price: item.p || 0,
        vatPct: 21,
        discountPct: 0,
        retentionPct: 0
      }));

      const dateStr = new Date().toISOString();
      await quotesApi.add({
        number: `BC3-${Date.now().toString().slice(-6)}`,
        date: dateStr,
        dueDate: dateStr,
        contactId: clientId!,
        status: 'draft',
        lines,
        notes: `Presupuesto importado desde archivo BC3: ${file?.name}`,
        baseTotal,
        vatTotal,
        retentionTotal: 0,
        grandTotal,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      } as any);
      
      addToast('Presupuesto importado y guardado correctamente', 'success');
    } catch (e: any) {
      addToast('Error al guardar: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const fmtEur = (n: number) => n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

  return (
    <div className="max-w-6xl mx-auto py-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-blue-500/15 rounded-xl">
          <FileText size={28} className="text-blue-400" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-100">Importar BC3</h1>
          <p className="text-sm text-gray-500">Analiza y convierte archivos BC3 (FIEBDC) a presupuestos mediante IA.</p>
        </div>
      </div>

      {/* Step Wizard Indicator */}
      <div className="mb-8 bg-[#0d1117] border border-white/8 rounded-xl p-5">
        <div className="flex items-center justify-between">
          {[
            { num: 1, label: 'Configurar', done: hasApiKey },
            { num: 2, label: 'Subir BC3', done: !!file },
            { num: 3, label: 'Procesar', done: items.length > 0 },
            { num: 4, label: 'Guardar', done: false },
          ].map((step, i, arr) => {
            const isActive = (
              (step.num === 1 && !hasApiKey) ||
              (step.num === 2 && hasApiKey && !file) ||
              (step.num === 3 && hasApiKey && file && items.length === 0) ||
              (step.num === 4 && items.length > 0)
            );
            return (
              <React.Fragment key={step.num}>
                <div className="flex flex-col items-center gap-1.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                    step.done ? 'bg-emerald-500 border-emerald-500 text-white' :
                    isActive ? 'bg-amber-500/20 border-amber-500 text-amber-400' :
                    'bg-white/5 border-white/10 text-gray-600'
                  }`}>
                    {step.done ? <CheckCircle2 size={14} /> : step.num}
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${
                    step.done ? 'text-emerald-400' : isActive ? 'text-amber-400' : 'text-gray-600'
                  }`}>{step.label}</span>
                </div>
                {i < arr.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 rounded-full ${step.done ? 'bg-emerald-500/50' : 'bg-white/5'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {!hasApiKey && (
        <div className="mb-6 p-5 rounded-xl border border-orange-500/30 bg-orange-500/5">
          <div className="flex items-center gap-2 mb-3">
            <Key size={16} className="text-orange-400" />
            <span className="text-sm font-bold text-white">Configura tu API Key de Gemini</span>
          </div>
          <p className="text-xs text-white/50 mb-3">Esta herramienta requiere Gemini 2.0. Pega tu API key abajo:</p>
          <div className="flex gap-2">
            <input type="password" value={apiKeyInput} onChange={e => setApiKeyInput(e.target.value)} 
                   className="flex-1 bg-gray-900 border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" 
                   placeholder="AIzaSy..." />
            <button onClick={() => { setGeminiKey(apiKeyInput); setApiKeySaved(true); addToast('API Key guardada', 'success'); }}
                    className="px-4 py-2 bg-orange-500/20 text-orange-300 rounded-lg text-sm font-bold hover:bg-orange-500/30 transition-colors">
              {apiKeySaved ? 'Guardada ✓' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 flex items-start gap-3">
            <X size={18} className="mt-0.5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto opacity-50 hover:opacity-100"><X size={14} /></button>
        </div>
      )}

      {!items.length ? (
        <div 
          className={`p-8 rounded-2xl bg-[#0d1117] border-2 border-dashed text-center transition-all duration-200 ${
            dragActive 
              ? 'border-blue-400 bg-blue-500/5 scale-[1.01]' 
              : 'border-white/10 hover:border-white/20'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
            <input ref={inputRef} type="file" accept=".bc3" className="hidden" onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
            
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 transition-all ${
              dragActive ? 'bg-blue-500/20 scale-110' : 'bg-white/5'
            }`}>
              <Upload size={32} className={`transition-colors ${dragActive ? 'text-blue-300' : 'text-blue-400'}`} />
            </div>

            <h3 className="text-xl font-bold text-white mb-2">
              {dragActive ? 'Suelta aquí tu archivo' : 'Sube tu archivo .bc3'}
            </h3>
            <p className="text-white/40 mb-6 text-sm max-w-sm mx-auto">
              {dragActive ? 'Soltando el archivo se cargará automáticamente' : 'Arrastra y suelta o selecciona tu archivo de mediciones procedente de Presto, Arquímedes u otros.'}
            </p>

            {file ? (
                <div className="mb-6 flex flex-col items-center">
                    <div className="flex items-center gap-2 text-emerald-400 mb-4 bg-emerald-500/10 px-4 py-2 rounded-full border border-emerald-500/20">
                        <CheckCircle2 size={16} />
                        <span className="text-sm font-medium">{file.name}</span>
                    </div>
                    <button onClick={processFile} disabled={loading} className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold transition-colors disabled:opacity-50">
                        {loading ? <span className="flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Analizando BC3...</span> : 'Procesar con IA'}
                    </button>
                    <button onClick={() => setFile(null)} className="mt-4 text-xs text-gray-500 hover:text-white">Cancelar</button>
                </div>
            ) : (
                <button onClick={() => inputRef.current?.click()} className="px-6 py-3 bg-white/10 hover:bg-white/15 text-white rounded-xl font-semibold transition-colors border border-white/10">
                    Seleccionar Archivo
                </button>
            )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* DDC Skills: Project Summary Panel                                   */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          {projectSummary && (
            <div className="bg-[#0d1117] border border-white/8 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowSummary(!showSummary)}
                className="w-full flex items-center justify-between px-5 py-3 border-b border-white/8 hover:bg-white/3 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <BarChart3 size={16} className="text-amber-400" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Resumen Económico (PEM + GG + BI + IVA)</h3>
                </div>
                {showSummary ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
              </button>
              {showSummary && (
                <div className="p-5">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {[
                      { label: 'PEM', value: projectSummary.pem, color: 'text-gray-200' },
                      { label: 'GG (13%)', value: projectSummary.gg, color: 'text-blue-400' },
                      { label: 'BI (6%)', value: projectSummary.bi, color: 'text-purple-400' },
                      { label: 'PEC', value: projectSummary.totalPEC, color: 'text-amber-400' },
                      { label: 'IVA (21%)', value: projectSummary.iva, color: 'text-orange-400' },
                      { label: 'Total con IVA', value: projectSummary.totalConIva, color: 'text-emerald-400' },
                    ].map((stat) => (
                      <div key={stat.label} className="bg-[#010409] rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{stat.label}</p>
                        <p className={`text-lg font-bold ${stat.color} mt-1`}>{fmtEur(stat.value)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm bg-blue-500" />
                      <span className="text-xs text-gray-400">Mano de obra ~{projectSummary.laborPct}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm bg-amber-500" />
                      <span className="text-xs text-gray-400">Materiales ~{projectSummary.materialPct}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm bg-purple-500" />
                      <span className="text-xs text-gray-400">Equipos ~15%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* DDC Skills: Waste Analysis Panel                                    */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          {wasteAnalysis && (
            <div className="bg-[#0d1117] border border-white/8 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowWaste(!showWaste)}
                className="w-full flex items-center justify-between px-5 py-3 border-b border-white/8 hover:bg-white/3 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <TrendingDown size={16} className="text-orange-400" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">
                    Análisis de Mermas — {fmtEur(wasteAnalysis.totalWasteCost)} extra ({wasteAnalysis.avgWastePct}%)
                  </h3>
                </div>
                {showWaste ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
              </button>
              {showWaste && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-white/5 text-gray-400 text-[10px] uppercase font-semibold">
                      <tr>
                        <th className="px-5 py-2">Partida</th>
                        <th className="px-3 py-2">Tipo Material</th>
                        <th className="px-3 py-2 text-right">Corte %</th>
                        <th className="px-3 py-2 text-right">Derrame %</th>
                        <th className="px-3 py-2 text-right">Rotura %</th>
                        <th className="px-3 py-2 text-right">Exceso %</th>
                        <th className="px-3 py-2 text-right">Total %</th>
                        <th className="px-3 py-2 text-right">Pedir</th>
                        <th className="px-3 py-2 text-right">Coste Merma</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {wasteAnalysis.items.slice(0, 20).map((w: WasteDetail, i: number) => (
                        <tr key={i} className="hover:bg-white/3 transition-colors">
                          <td className="px-5 py-2 text-gray-300 max-w-[200px] truncate" title={items[i]?.label}>{items[i]?.label.slice(0, 40)}</td>
                          <td className="px-3 py-2">
                            <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-white/5 text-gray-400 border border-white/10">
                              {w.materialType}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right text-gray-500">{w.cuttingPct > 0 ? `${w.cuttingPct}%` : '—'}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{w.spillagePct > 0 ? `${w.spillagePct}%` : '—'}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{w.breakagePct > 0 ? `${w.breakagePct}%` : '—'}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{w.overrunPct > 0 ? `${w.overrunPct}%` : '—'}</td>
                          <td className="px-3 py-2 text-right font-bold text-orange-400">{w.totalWastePct}%</td>
                          <td className="px-3 py-2 text-right text-blue-400">{w.orderQuantity}</td>
                          <td className="px-3 py-2 text-right font-semibold text-orange-300">{w.wasteCost > 0 ? fmtEur(w.wasteCost) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 border-white/10">
                      <tr>
                        <td colSpan={8} className="px-5 py-3 text-right text-xs uppercase tracking-wider text-gray-500 font-bold">Coste total de mermas</td>
                        <td className="px-3 py-3 text-right text-lg font-bold text-orange-400">{fmtEur(wasteAnalysis.totalWasteCost)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* DDC Skills: VE Targets Panel                                        */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          {veTargets.length > 0 && (
            <div className="bg-[#0d1117] border border-white/8 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowVE(!showVE)}
                className="w-full flex items-center justify-between px-5 py-3 border-b border-white/8 hover:bg-white/3 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Lightbulb size={16} className="text-emerald-400" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">
                    Optimización VE — {veTargets.length} partidas objetivo
                  </h3>
                </div>
                {showVE ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
              </button>
              {showVE && (
                <div className="p-5 space-y-3">
                  {veTargets.map((target, i) => {
                    const pctWidth = Math.min(target.pct * 3, 100);
                    return (
                      <div key={i} className="bg-[#010409] rounded-lg p-4 border border-white/5">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-200 font-medium truncate">{target.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{fmtEur(target.total)} · {target.pct}% del presupuesto</p>
                          </div>
                          <span className="text-xs font-bold ml-3 flex-shrink-0">{target.veOpportunity}</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ${
                              target.pct > 10 ? 'bg-red-500' : target.pct > 5 ? 'bg-amber-500' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${pctWidth}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* Partidas Table with AI buttons                                      */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          <div className="rounded-2xl bg-[#0d1117] border border-white/10 overflow-hidden">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
                <div>
                    <h3 className="font-bold text-white">Partidas Extraídas</h3>
                    <p className="text-xs text-gray-500">{items.length} elementos detectados en {file?.name} — Haz clic en cualquier celda para editar</p>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={() => setItems([])} className="text-sm text-gray-400 hover:text-white transition-colors">Volver</button>
                    <button onClick={saveToQuote} disabled={saving} className="px-5 py-2.5 bg-green-600 hover:bg-green-500 text-white rounded-lg font-semibold text-sm flex items-center gap-2 disabled:opacity-50 transition-colors">
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        Guardar como Presupuesto
                    </button>
                </div>
            </div>

            {/* AI Popover */}
            {aiPopover && (
              <div className="mx-5 mt-4 p-4 rounded-xl border bg-[#010409] border-purple-500/30 relative">
                <button onClick={() => setAiPopover(null)} className="absolute top-2 right-2 text-gray-500 hover:text-white"><X size={14} /></button>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles size={14} className="text-purple-400" />
                  <span className="text-xs font-bold uppercase tracking-wider text-purple-400">
                    {aiPopover.type === 'explain' ? 'Explicación IA' : 'Alternativas IA'}
                  </span>
                  <span className="text-[10px] text-gray-600">— {items[aiPopover.idx]?.label.slice(0, 50)}</span>
                </div>
                {aiPopover.loading ? (
                  <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
                    <Loader2 size={14} className="animate-spin text-purple-400" />
                    Consultando a Llama 3.3…
                  </div>
                ) : (
                  <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">{aiPopover.content}</div>
                )}
              </div>
            )}
            
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="bg-white/5 text-gray-400 text-xs uppercase font-semibold">
                        <tr>
                            <th className="px-5 py-3">Código</th>
                            <th className="px-5 py-3">Descripción</th>
                            <th className="px-5 py-3 text-right">Cantidad</th>
                            <th className="px-5 py-3 text-center">Unidad</th>
                            <th className="px-5 py-3 text-right">Precio Ud.</th>
                            <th className="px-5 py-3 text-right">Total</th>
                            <th className="px-5 py-3 w-24">IA</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {items.map((item, i) => (
                            <tr key={i} className="hover:bg-white/5 transition-colors group">
                                <td className="px-5 py-3">
                                  {editingIdx === i ? (
                                    <input value={item.cod} onChange={e => updateItem(i, 'cod', e.target.value)} 
                                      className="bg-white/5 border border-white/20 rounded px-2 py-1 text-sm text-gray-200 w-full font-mono focus:outline-none focus:border-blue-500"
                                      onBlur={() => setEditingIdx(null)} autoFocus />
                                  ) : (
                                    <span className="font-mono text-gray-400 cursor-pointer hover:text-white" onClick={() => setEditingIdx(i)}>{item.cod}</span>
                                  )}
                                </td>
                                <td className="px-5 py-3">
                                  {editingIdx === i ? (
                                    <input value={item.label} onChange={e => updateItem(i, 'label', e.target.value)}
                                      className="bg-white/5 border border-white/20 rounded px-2 py-1 text-sm text-gray-200 w-full focus:outline-none focus:border-blue-500" />
                                  ) : (
                                    <span className="text-gray-200 cursor-pointer hover:text-white" onClick={() => setEditingIdx(i)}>{item.label}</span>
                                  )}
                                </td>
                                <td className="px-5 py-3 text-right">
                                  {editingIdx === i ? (
                                    <input type="number" step="0.01" value={item.quantity} onChange={e => updateItem(i, 'quantity', parseFloat(e.target.value) || 0)}
                                      className="bg-white/5 border border-white/20 rounded px-2 py-1 text-sm text-right text-blue-400 w-20 focus:outline-none focus:border-blue-500" />
                                  ) : (
                                    <span className="font-medium text-blue-400 cursor-pointer hover:text-blue-300" onClick={() => setEditingIdx(i)}>{item.quantity}</span>
                                  )}
                                </td>
                                <td className="px-5 py-3 text-center text-gray-500">{item.ud}</td>
                                <td className="px-5 py-3 text-right">
                                  {editingIdx === i ? (
                                    <input type="number" step="0.01" value={item.p} onChange={e => updateItem(i, 'p', parseFloat(e.target.value) || 0)}
                                      className="bg-white/5 border border-white/20 rounded px-2 py-1 text-sm text-right text-emerald-400 w-24 focus:outline-none focus:border-blue-500" />
                                  ) : (
                                    <span className="text-emerald-400 cursor-pointer hover:text-emerald-300" onClick={() => setEditingIdx(i)}>{item.p?.toLocaleString('es-ES', {style: 'currency', currency: 'EUR'}) || '0 €'}</span>
                                  )}
                                </td>
                                <td className="px-5 py-3 text-right font-bold text-white">{((item.quantity || 0) * (item.p || 0)).toLocaleString('es-ES', {style: 'currency', currency: 'EUR'})}</td>
                                <td className="px-5 py-3">
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                    {hasGroqKey && (
                                      <>
                                        <button
                                          onClick={() => handleExplain(i)}
                                          className="p-1.5 text-gray-500 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-all"
                                          title="Explicar partida (IA)"
                                        >
                                          <Info size={14} />
                                        </button>
                                        <button
                                          onClick={() => handleSuggestAlternatives(i)}
                                          className="p-1.5 text-gray-500 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-all"
                                          title="Sugerir alternativas (IA)"
                                        >
                                          <Lightbulb size={14} />
                                        </button>
                                      </>
                                    )}
                                    <button 
                                      onClick={() => setEditingIdx(editingIdx === i ? null : i)}
                                      className="p-1.5 text-gray-500 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-all"
                                      title="Editar partida"
                                    >
                                      <Edit3 size={14} />
                                    </button>
                                  </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {/* ── Totals Footer ─────────────────────────────── */}
                    <tfoot className="border-t-2 border-white/10 bg-white/3">
                      <tr>
                        <td colSpan={5} className="px-5 py-3 text-right text-xs uppercase tracking-wider text-gray-500 font-bold">Base Imponible</td>
                        <td className="px-5 py-3 text-right font-bold text-gray-200">{baseTotal.toLocaleString('es-ES', {style: 'currency', currency: 'EUR'})}</td>
                        <td></td>
                      </tr>
                      <tr>
                        <td colSpan={5} className="px-5 py-2 text-right text-xs uppercase tracking-wider text-gray-500 font-bold">IVA (21%)</td>
                        <td className="px-5 py-2 text-right font-medium text-gray-400">{vatTotal.toLocaleString('es-ES', {style: 'currency', currency: 'EUR'})}</td>
                        <td></td>
                      </tr>
                      <tr>
                        <td colSpan={5} className="px-5 py-3 text-right text-sm uppercase tracking-wider text-amber-400 font-bold">Total</td>
                        <td className="px-5 py-3 text-right text-lg font-bold text-amber-400">{grandTotal.toLocaleString('es-ES', {style: 'currency', currency: 'EUR'})}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
